import React, { forwardRef, ForwardedRef, AriaRole } from 'react';
import { useObjectRef } from '@react-aria/utils';
import { useButton, AriaButtonProps } from 'react-aria';
import classnames from 'classnames';
import { DSIconElement, IconChevronRightOutline } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography';

export type GenericButtonProps = {
  dataQa?: string;
  disabled?: boolean;
  hasSubmenu?: boolean;
  icon: DSIconElement;
  iconHightlight?: DSIconElement;
  isHighlighted?: boolean;
  isPopoverTrigger?: boolean;
  label: string;
  onPress?: (event) => void;
  ref?: ForwardedRef<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  role?: AriaRole,
  status?: boolean;
} & AriaButtonProps;

const getStatusLabelForStatus = (status: boolean | undefined): string | null => {
  if (status !== undefined) {
    return status ? 'On' : 'Off';
  }

  return null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GenericButton: React.FC<GenericButtonProps> = forwardRef<any, GenericButtonProps>(({
  dataQa,
  disabled = false,
  hasSubmenu = false,
  icon: Icon,
  iconHightlight: IconHighlight,
  isHighlighted = false,
  isPopoverTrigger = false,
  role,
  label,
  onPress = () => undefined,
  status,
  ...ariaButtonProps
}, forwardedRef) => {
  const ref = useObjectRef(forwardedRef);
  const { buttonProps, isPressed } = useButton({ onPress, isDisabled: disabled }, ref);
  const statusLabel = getStatusLabelForStatus(status);

  return (
    <button
      {...buttonProps}
      {...ariaButtonProps}
      ref={ref}
      aria-label={label}
      className={classnames(
        `group
          w-full h-6 border-none border-0 py-1.5 pl-2
          flex items-center
          bg-white text-medium-emphasis outline-none cursor-pointer
          first:rounded-t last:rounded-b
          focus:outline-none focus:ring focus:ring-blue-100 ring-inset`,
        {
          'pr-1': hasSubmenu,
          'pr-3': !hasSubmenu,
          'bg-sds-primary-10': isHighlighted && !disabled,
          'hover:bg-gray-50 active:bg-sds-primary-10': !disabled && !isHighlighted,
          'cursor-not-allowed': disabled,
        },
      )}
      data-qa={dataQa}
      role={role}
      type="button"
    >
      <span
        aria-label={Icon.displayName}
        className={classnames('mr-1',
          {
            'text-sds-primary-70': isHighlighted && !disabled,
            'text-disabled-emphasis': disabled,
            'group-hover:text-sds-primary-70': !disabled,
          },
        )}
        role="img"
      >
        {(IconHighlight && (isHighlighted || (isPopoverTrigger && isPressed)) ?
          <IconHighlight className="w-2.5 h-2.5 fill-current" /> :
          <Icon className="w-2.5 h-2.5 fill-current" />
        )}
      </span>

      <Typography
        className={classnames('font-semibold whitespace-nowrap',
          {
            'text-high-emphasis': (isHighlighted || isPressed) && !disabled,
            'text-disabled-emphasis': disabled,
          },
        )}
        variant="body"
      >
        {label}
      </Typography>

      {statusLabel && (
        <span
          aria-label={statusLabel}
          className={classnames('ml-1',
            {
              'text-sds-primary-60': status && !disabled,
              'text-disabled-emphasis': !status || disabled,
            },
          )}
          role="status"
        >
          {statusLabel}
        </span>
      )}

      {hasSubmenu && (
        <IconChevronRightOutline
          aria-label="has submenu"
          className={classnames('h-2 ml-auto fill-current',
            {
              'text-gray-500': !disabled,
              'text-disabled-emphasis': disabled,
            },
          )}
          role="img"
        />
      )}
    </button>
  );
});

GenericButton.displayName = 'LiveToolbarButton';

export default GenericButton;
